import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { MenuItemForListDto } from "@dtos/menu-items/menu-items-for-list-dto";
import { Storage } from "@ionic/storage-angular";
import { MessageBusService } from "@services/message-bus.service";
import { ValuesService } from "@services/values.service";
import { PixelService } from "ngx-pixel-eventid";
import { constants } from "../shared/constants/variables";
import { CartItem } from "../_models/cart-items";
import { InventoryService } from "./inventory.service";
import { LoggerService } from "./logger.service";

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private readonly _storageKey = constants.storageKeys.cart;
    private readonly _storage = inject(Storage)
    private readonly _inventoryService = inject(InventoryService);
    private readonly _logger = inject(LoggerService);
    private readonly _pixel = inject(PixelService);
    private readonly _messageBus = inject(MessageBusService);
    private readonly _valuesService = inject(ValuesService);
    private readonly _restrictions = this._valuesService.restrictions.asReadonly();

    cartItems = signal<CartItem[] | undefined>(undefined);
    hasOutOfStockItems = computed(() => this.cartItems() && this.cartItems().some(x => x.isOutOfStock));
    cartTotal = computed(() => {
        this._logger.debug('CartService -- computing cart total...');
        const cartItems = this.cartItems();
        if (!cartItems?.length) return 0;
        return cartItems
            .filter(x => !x.isOutOfStock)
            .reduce((acc, cartItem) => acc + cartItem.menuItem.price * cartItem.quantity, 0);
    });
    isTotalAboveMinimum = computed(() => {
        const restrictions = this._restrictions();
        if (!restrictions) return false;
        if (!restrictions.length) return true;

        const minimums = restrictions.map(x => x.minimumOrderAmount);
        return this.cartTotal() >= Math.max(...minimums);
    });
    isTotalBelowMaximum = computed(() => {
        const restrictions = this._restrictions();
        if (!restrictions?.length) return true;
        const maximums = restrictions
            .map(x => x.maximumOrderAmount)
            .filter((x): x is number => x != null);
        if (!maximums.length) return true;
        const strictestMaximum = Math.min(...maximums);
        return this.cartTotal() <= strictestMaximum;
    });

    constructor() {
        this._storage.get(this._storageKey)
            .then(storedCartItems => {
                this._logger.debug('CartService -- loaded cart items from storage', storedCartItems);
                this.cartItems.set(storedCartItems ?? []);
            });

        toObservable(this.cartItems)
            .pipe(takeUntilDestroyed())
            .subscribe(async cartItems => {
                if (cartItems === undefined) return;
                await this._storage.set(this._storageKey, cartItems);
            });

        toObservable(this._inventoryService.outOfStockItems)
            .pipe(takeUntilDestroyed())
            .subscribe(async outOfStockItems => {
                this._logger.debug('CartService -- got out of stock items', {cart: this.cartItems(), outOfStockItems});

                this.cartItems.update(cartItems => {
                    if (!cartItems?.length) return cartItems;

                    this._logger.debug('CartService -- settings cart items from change in out of stock');
                    return cartItems.map(i => ({
                        ...i,
                        isOutOfStock: outOfStockItems.some(x => x.menuItemId === i.menuItem.id),
                    }));
                });
            });

        this._messageBus.onOrderAccepted
            .pipe(takeUntilDestroyed())
            .subscribe(async accepted => {
                if (!accepted) return;
                this._logger.debug('CartService -- order accepted, clearing cart');
                await this.clearCart();
            });
    }

    updateCartQuantity(menuItem: MenuItemForListDto, quantity: number) {
        this._logger.debug('CartService -- updateCartQuantity called', menuItem, quantity);

        if (quantity <= 0) {
            this._logger.debug('CartService -- removing cart item', menuItem);
            this.cartItems.update(cartItems => {
                if (!cartItems?.length) return cartItems;
                return [...cartItems.filter(x => x.menuItem.id !== menuItem.id)];
            });
        } else {
            this._logger.debug('CartService -- setting cart item quantity', menuItem, quantity);

            this.cartItems.update((cartItems = []) => {
                const idx = cartItems.findIndex(x => x.menuItem.id === menuItem.id);

                if (idx >= 0) {
                    const oldQuantity = cartItems[idx].quantity;

                    this._pixel.track('AddToCart', {
                        content_ids: [menuItem.id],
                        content_name: menuItem.name,
                        content_type: 'product',
                        value: quantity * menuItem.price,
                        currency: 'PHP',
                        num_items: quantity - oldQuantity
                    });

                    return cartItems.map((item, i) => i === idx ? {...item, quantity} : item);
                } else {
                    this._pixel.track('AddToCart', {
                        content_ids: [menuItem.id],
                        content_name: menuItem.name,
                        content_type: 'product',
                        value: quantity * menuItem.price,
                        currency: 'PHP',
                        num_items: quantity
                    });

                    return [
                        ...cartItems,
                        {
                            quantity,
                            isOutOfStock: this._inventoryService.outOfStockItems().some(x => x.menuItemId === menuItem.id),
                            menuItem
                        }
                    ];
                }
            });
        }
    }

    clearCart() {
        this._logger.debug('CartService -- clearing cart');
        this.cartItems.set([]);
    }
}
